import { useState } from "react";
import { UserPlus, Mail, UserMinus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { Items, MemberDTO } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { useMembers, invalidateWorkspace } from "@/features/data";
import { Page } from "@/components/Page";
import { Badge, Button, Card, ErrorState, Field, IconButton, Input, Spinner } from "@/components/ui";
import { Select } from "@/components/Select";
import { Dialog } from "@/components/Dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast, errorMessage } from "@/components/Toast";
import { queryClient } from "@/app/query";
import { centsToInput, inputToCents } from "@/lib/format";

interface Invitation { id: string; email: string; role: string; expires_at: string }

export function TeamPage() {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const { toast } = useToast();
  const isAdmin = workspace?.role !== "member";
  const { data: members, isLoading, isError, refetch } = useMembers();
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: invites } = useQuery({
    queryKey: ["invitations", wid],
    queryFn: () => api.get<Items<Invitation>>(`/workspaces/${wid}/invitations`).then((r) => r.items),
    enabled: isAdmin,
  });

  async function revoke(inv: Invitation) {
    try {
      await api.del(`/workspaces/${wid}/invitations/${inv.id}`);
      void queryClient.invalidateQueries({ queryKey: ["invitations", wid] });
      toast("Invitation revoked", "info");
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <Page
      title="Team"
      description="Members of this workspace."
      actions={isAdmin && <Button size="sm" onClick={() => setInviteOpen(true)}><UserPlus className="size-4" /> Invite</Button>}
    >
      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <div className="space-y-6">
          <Card>
            {(members ?? []).map((m, i, arr) => (
              <MemberRow key={m.user_id} m={m} last={i === arr.length - 1} isAdmin={isAdmin} />
            ))}
          </Card>
          {isAdmin && (invites?.length ?? 0) > 0 && (
            <div>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-faint">Pending invitations</h2>
              <Card>
                {invites!.map((inv) => (
                  <div key={inv.id} className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-b-0">
                    <Mail className="size-4 text-faint" />
                    <span className="flex-1 text-sm text-ink">{inv.email}</span>
                    <Badge className="bg-elevated text-muted">{inv.role}</Badge>
                    <button className="text-xs text-muted hover:text-danger" onClick={() => revoke(inv)}>
                      Revoke
                    </button>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </div>
      )}
      {inviteOpen && <InviteDialog onClose={() => setInviteOpen(false)} />}
    </Page>
  );
}

function MemberRow({ m, last, isAdmin }: { m: MemberDTO; last: boolean; isAdmin: boolean }) {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const editable = isAdmin && !m.self && m.role !== "owner";

  async function patch(body: Record<string, unknown>) {
    try {
      await api.patch(`/workspaces/${wid}/members/${m.user_id}`, body);
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  function saveRate(text: string) {
    const cents = inputToCents(text);
    if (cents === undefined) {
      toast("Enter the rate as a number, e.g. 950", "error");
      return;
    }
    if (cents !== (m.hourly_rate_cents ?? null)) void patch({ hourly_rate_cents: cents });
  }

  async function remove() {
    await api.del(`/workspaces/${wid}/members/${m.user_id}`);
    invalidateWorkspace(wid);
    toast(`${m.name} removed from the workspace`, "info");
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${!last ? "border-b border-line" : ""}`}>
      <div className="flex size-8 items-center justify-center rounded-full bg-elevated text-xs font-semibold text-muted">
        {m.name[0]?.toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{m.name} {m.self && <span className="text-faint">(you)</span>}</p>
        <p className="truncate text-xs text-muted">{m.email}</p>
      </div>
      {isAdmin && m.default_visibility && m.default_visibility !== "visible" && (
        <Badge className="bg-elevated capitalize text-faint" >{m.default_visibility}</Badge>
      )}
      {isAdmin ? (
        <div className="relative">
          <Input
            key={m.hourly_rate_cents ?? "unset"}
            defaultValue={centsToInput(m.hourly_rate_cents)}
            onBlur={(e) => saveRate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            placeholder="—"
            inputMode="decimal"
            aria-label={`Hourly rate for ${m.name}`}
            className="mono h-8 w-24 pr-7 text-right text-xs tabular-nums"
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-faint">/h</span>
        </div>
      ) : (
        m.hourly_rate_cents != null && <span className="mono text-xs tabular-nums text-muted">{centsToInput(m.hourly_rate_cents)}/h</span>
      )}
      {editable ? (
        <Select
          value={m.role}
          onChange={(e) => void patch({ role: e.target.value })}
          aria-label={`Role for ${m.name}`}
          className="h-8 text-xs"
        >
          <option value="member">member</option>
          <option value="admin">admin</option>
        </Select>
      ) : (
        <Badge className="bg-elevated text-muted">{m.role}</Badge>
      )}
      {editable && (
        <IconButton label={`Remove ${m.name}`} onClick={() => setConfirmRemove(true)} className="hover:text-danger">
          <UserMinus className="size-4" />
        </IconButton>
      )}
      {confirmRemove && (
        <ConfirmDialog
          title={`Remove ${m.name}?`}
          description="Their tracked time stays in the workspace, but they lose all access. This can't be undone from here."
          confirmLabel="Remove member"
          onConfirm={remove}
          onClose={() => setConfirmRemove(false)}
        />
      )}
    </div>
  );
}

function InviteDialog({ onClose }: { onClose: () => void }) {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wid}/invitations`, { email, role });
      void queryClient.invalidateQueries({ queryKey: ["invitations", wid] });
      toast("Invitation sent", "success");
      onClose();
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Invite a member" description="They'll receive an e-mail with an accept link.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="E-mail"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)} className="w-full">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </Select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" loading={busy} disabled={!email}>Send invite</Button>
        </div>
      </form>
    </Dialog>
  );
}
